import { useState } from "react";
import { useInterval } from "react-use";
import { useTranslation } from "../../lib";
import { useSelectTheme } from "../../store";
import { formatSignOutCountdown } from "./idle_sign_out_warning_pop";

export interface SessionCapWarningPropsT {
  /** When the session reaches its cap and ends, whatever the user does. */
  endsAt: number;
}

/**
 * The notice before the session's cap. Unlike the idle warning there is nothing to answer: no input
 * extends the cap. So it is no dialog and blocks nothing -- the user's job now is to save what they
 * are working on, and every control on the page has to stay within reach. It takes no keys either,
 * so Escape still closes whatever popup the user has open.
 */
export const SessionCapWarning = (props: SessionCapWarningPropsT) => {
  const { endsAt } = props;
  const { t } = useTranslation();
  const { color, pad, fontSize, fontWeight, borderRadius } = useSelectTheme();

  const [nowMs, setNowMs] = useState(() => Date.now());
  useInterval(() => setNowMs(Date.now()), 1000);

  return (
    // A small card in a corner rather than a bar, so it covers as little of a popup's controls as it
    // can; above every layer, as the idle warning is, since it mounts outside the router too.
    <div
      data-testid="session_cap_warning"
      css={{
        position: "fixed",
        left: pad.large,
        bottom: pad.large,
        zIndex: 100000,
        boxSizing: "border-box",
        width: "360px",
        maxWidth: `calc(100vw - 2 * ${pad.large})`,
        padding: pad.large,
        borderRadius: borderRadius.default,
        border: `1px solid ${color.border.warning}`,
        background: color.surface.attention,
        color: color.textIcon.title,
        fontSize: fontSize.h6,
        boxShadow: "0px 2px 10px rgba(0, 0, 0, 0.13)",
      }}
    >
      <p role="alert" css={{ margin: `0 0 ${pad.small}`, fontWeight: fontWeight.medium }}>
        {t("session_cap.warning_message")}
      </p>
      {/* Outside the alert: a number that changes every second would be read out every second. */}
      <p
        data-testid="session_cap_countdown"
        css={{ margin: 0, fontWeight: fontWeight.bold, fontVariantNumeric: "tabular-nums" }}
      >
        {t("session_cap.countdown", { time: formatSignOutCountdown(endsAt - nowMs) })}
      </p>
    </div>
  );
};
